import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import REMINDERS from "../api/services/REMINDERS";
import CUSTOMERS from "../api/services/CUSTOMERS";
import styles from "../styles/_components/createReminderModal.module.scss";

const RECURRENCE_OPTIONS = [
  { value: "none", label: "ללא" },
  { value: "daily", label: "יומי" },
  { value: "weekly", label: "שבועי" },
  { value: "monthly", label: "חודשי" },
];

const CreateReminderModal = ({ isOpen, onClose, clientId: propClientId, clientName: propClientName }) => {
  const [step, setStep] = useState(1);
  const [input, setInput] = useState("");
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Step 2 fields
  const [title, setTitle] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [recurrence, setRecurrence] = useState("none");
  const [pushEnabled, setPushEnabled] = useState(false);

  // Client tagging
  const [selectedClientId, setSelectedClientId] = useState(propClientId || null);
  const [selectedClientName, setSelectedClientName] = useState(propClientName || "");
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);

  const searchClients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setClientResults([]);
      return;
    }
    setSearchingClients(true);
    try {
      const res = await CUSTOMERS.get(1, 10, "confirmed", query);
      const rawClients = res?.data?.data || res?.data?.results || res?.data || [];
      // API returns {user: {name, phone, _id}, userId} - flatten
      const clients = (Array.isArray(rawClients) ? rawClients : []).map(c => ({
        _id: c.user?._id || c.userId || c._id,
        name: c.user?.name || c.name || "",
        phone: c.user?.phone || c.phone || "",
      })).filter(c => c.name || c.phone);
      setClientResults(clients);
    } catch (e) {
      console.log("Client search error:", e);
      setClientResults([]);
    }
    setSearchingClients(false);
  }, []);

  if (!isOpen) return null;

  const handleClose = () => {
    // reset state
    setStep(1);
    setInput("");
    setTitle("");
    setScheduledAt("");
    setRecurrence("none");
    setPushEnabled(false);
    setSelectedClientId(propClientId || null);
    setSelectedClientName(propClientName || "");
    setShowClientSearch(false);
    setClientQuery("");
    setClientResults([]);
    onClose(false);
  };

  const handleParse = async () => {
    if (!input.trim()) return;
    setParsing(true);
    try {
      const res = await REMINDERS.parse_reminder({ input: input.trim() });
      const parsed = res?.data?.data || res?.data || {};
      setTitle(parsed.title || input.trim());
      if (parsed.dueDate || parsed.scheduledAt) {
        // Convert ISO to datetime-local format
        const dt = new Date(parsed.dueDate || parsed.scheduledAt);
        const pad = (n) => String(n).padStart(2, "0");
        const local = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        setScheduledAt(local);
      }
      if (parsed.recurrence) setRecurrence(parsed.recurrence);
      setStep(2);
    } catch (err) {
      console.log(err);
      // Even on parse failure, let the user fill manually
      setTitle(input.trim());
      setStep(2);
    }
    setParsing(false);
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error("נא להזין כותרת לתזכורת");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        rawInput: input.trim(),
        title: title.trim(),
        dueDate: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        recurrence: { type: recurrence },
        sendPush: pushEnabled,
        ...(selectedClientId ? { clientId: selectedClientId } : {}),
      };
      await REMINDERS.create_reminder(payload);
      toast.success("תזכורת נוצרה בהצלחה");
      setStep(1);
      setInput("");
      setTitle("");
      setScheduledAt("");
      setRecurrence("none");
      setPushEnabled(false);
      onClose(true);
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || "שגיאה ביצירת התזכורת");
    }
    setSaving(false);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.backdrop} onClick={handleClose} />
      <div className={styles.modal} dir="rtl">
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>תזכורת חדשה</h2>
          <button className={styles.closeBtn} onClick={handleClose} aria-label="סגור">
            ×
          </button>
        </div>

        {step === 1 && (
          <div className={styles.step}>
            <p className={styles.stepHint}>תאר את התזכורת שלך בטבעית</p>
            <textarea
              className={styles.textarea}
              dir="rtl"
              placeholder='לדוגמה: "להתקשר לדוד מחר בשעה 10"'
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={4}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleParse();
              }}
            />
            <button
              className={styles.primaryBtn}
              onClick={handleParse}
              disabled={parsing || !input.trim()}
            >
              {parsing ? "מעבד..." : "המשך"}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className={styles.step}>
            {/* Title */}
            <div className={styles.field}>
              <label className={styles.label}>כותרת</label>
              <input
                className={styles.input}
                dir="rtl"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="כותרת התזכורת"
              />
            </div>

            {/* Date & Time */}
            <div className={styles.field}>
              <label className={styles.label}>תאריך ושעה</label>
              <input
                className={styles.input}
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>

            {/* Client */}
            <div className={styles.field}>
              <label className={styles.label}>לקוח</label>
              {selectedClientId ? (
                <div className={styles.clientChipRow}>
                  <span className={styles.clientBadge}>{selectedClientName}</span>
                  <button
                    className={styles.clientRemoveBtn}
                    onClick={() => { setSelectedClientId(null); setSelectedClientName(""); }}
                  >×</button>
                </div>
              ) : showClientSearch ? (
                <div className={styles.clientSearchWrap}>
                  <input
                    className={styles.clientSearchInput}
                    type="text"
                    dir="rtl"
                    placeholder="הקלד שם לקוח..."
                    value={clientQuery}
                    onChange={(e) => {
                      setClientQuery(e.target.value);
                      searchClients(e.target.value);
                    }}
                    autoFocus
                  />
                  {searchingClients && <div className={styles.clientSearchLoading}>מחפש...</div>}
                  {clientResults.length > 0 && (
                    <div className={styles.clientResultsList}>
                      {clientResults.map((c) => (
                        <button
                          key={c._id || c.id}
                          className={styles.clientResultItem}
                          onClick={() => {
                            setSelectedClientId(c._id || c.id);
                            setSelectedClientName(c.name || c.phone);
                            setShowClientSearch(false);
                            setClientQuery("");
                            setClientResults([]);
                          }}
                        >
                          <span>{c.name || c.phone}</span>
                          {c.phone && c.name && <span className={styles.clientResultPhone}>{c.phone}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {clientQuery.length >= 2 && !searchingClients && clientResults.length === 0 && (
                    <div className={styles.clientNoResults}>לא נמצאו לקוחות</div>
                  )}
                  <button className={styles.clientSearchCancel} onClick={() => { setShowClientSearch(false); setClientQuery(""); setClientResults([]); }}>
                    ביטול
                  </button>
                </div>
              ) : (
                <button className={styles.tagClient} onClick={() => setShowClientSearch(true)}>
                  @ תייג לקוח
                </button>
              )}
            </div>

            {/* Recurrence */}
            <div className={styles.field}>
              <label className={styles.label}>חזרות</label>
              <div className={styles.recurrenceRow}>
                {RECURRENCE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    className={`${styles.recurrenceBtn} ${recurrence === opt.value ? styles.recurrenceBtnActive : ""}`}
                    onClick={() => setRecurrence(opt.value)}
                    type="button"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Push notification toggle */}
            <div className={styles.fieldRow}>
              <label className={styles.label}>התראת Push</label>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={pushEnabled}
                  onChange={(e) => setPushEnabled(e.target.checked)}
                />
                <span className={styles.toggleSlider} />
              </label>
            </div>

            {/* Actions */}
            <div className={styles.actions}>
              <button
                className={styles.secondaryBtn}
                onClick={() => setStep(1)}
                disabled={saving}
              >
                חזור
              </button>
              <button
                className={styles.primaryBtn}
                onClick={handleCreate}
                disabled={saving}
              >
                {saving ? "שומר..." : "צור תזכורת"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateReminderModal;
